import React from "react";
import { motion } from "framer-motion";
import { Check, Star, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { PreorderProgress } from "./PreorderProgress";

export const Tiers = ({ tiers, shipDate, stats, onSelect }) => {
  return (
    <section id="tiers" data-testid="pricing-section" className="relative py-24 md:py-32 bg-sf-surface/30">
      <div className="w-full max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-sf-ember" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-sf-ember">The Tiers</span>
          </div>
          <h2 className="sf-stencil text-4xl md:text-5xl text-sf-bone max-w-3xl leading-[1.05]">
            Reserve your copy.
          </h2>
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-sf-muted">
            Reserve free now · No payment until we reach 300 · Ships {shipDate}
          </p>
          <PreorderProgress stats={stats} />
        </Reveal>

        <div className={`mt-14 grid gap-6 items-stretch mx-auto ${tiers.length > 1 ? "sm:grid-cols-2 max-w-3xl" : "max-w-md"}`}>
          {tiers.map((tier, i) => {
            const popular = tier.popular;
            const showBadge = tier.popular && tiers.length > 1;
            return (
              <Reveal key={tier.id} delay={i * 0.1} className="h-full">
                <div
                  data-testid="pricing-tier-card"
                  className={`relative h-full flex flex-col rounded-md p-8 border transition-all duration-300 ${
                    popular
                      ? "border-sf-ember bg-sf-surfaceHover sf-ember-glow"
                      : "border-sf-border bg-sf-surface hover:border-sf-dim/40"
                  }`}
                >
                  {showBadge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-gradient-to-r from-sf-emberDeep to-sf-emberBright text-white font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm">
                      <Star size={11} fill="currentColor" /> Most Popular
                    </div>
                  )}

                  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-sf-dim">{tier.subtitle}</div>
                  <h3 className="mt-2 sf-stencil text-2xl tracking-wide text-sf-bone">{tier.name}</h3>

                  <div className="mt-5 flex items-baseline gap-1">
                    <span className="sf-stencil text-5xl text-sf-bone">${Number(tier.price).toFixed(0)}</span>
                    <span className="font-mono text-xs uppercase tracking-widest text-sf-muted">later</span>
                  </div>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-sf-ember">
                    $0 today · reserve free
                  </p>

                  <ul className="mt-7 space-y-3 flex-1">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-sf-dim leading-relaxed">
                        <Check size={16} className={`mt-0.5 shrink-0 ${popular ? "text-sf-ember" : "text-sf-steel"}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <motion.button
                    data-testid="pricing-checkout-button"
                    onClick={() => onSelect(tier)}
                    animate={{
                      scale: [1, 1.03, 1],
                      boxShadow: [
                        "0 0 12px rgba(210,84,51,0.4)",
                        "0 0 34px rgba(232,112,58,0.8)",
                        "0 0 12px rgba(210,84,51,0.4)",
                      ],
                    }}
                    transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="group mt-8 w-full inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-sf-emberDeep to-sf-emberBright text-white font-stencil uppercase tracking-[0.15em] text-base px-6 py-4 rounded-sm ring-2 ring-sf-emberBright/60"
                  >
                    Reserve My Deck — Free
                    <ArrowRight size={20} className="transition-transform group-hover:translate-x-1.5" />
                  </motion.button>
                  <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-sf-muted">
                    No payment until we hit 300 · Ships {shipDate}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Tiers;
